import MainPageLayout from 'components/layout/MainPageLayout';
import AccessibilityStatementPage from 'components/legal/AccessibilityStatementPage';
import LoginPage from 'components/login/LoginPage';
import RedirectPage from 'components/login/RedirectPage';
import SessionChecker from 'components/SessionChecker';
import ProtectedRoute from 'components/shared/ProtectedRoute';
import UserGuard from 'components/UserGuard';
import { tenantConfig } from 'config';
import React, { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import authClient from 'services/AuthService';
import routes from 'store/configs/Routes';
import { OnlineVerification } from './credentials/OnlineVerification/OnlineVerification';
import { PublicBadgeDetailsPage } from './credentials/PublicBadgeDetailsPage/PublicBadgeDetailsPage';
import LogoutLandingPage from './login/LogoutLandingPage/LogoutLandingPage';
import SessionTabsContext from 'components/SessionTabsContext';

const CheckAuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  if (tenantConfig.loginUrl) {
    window.location.assign(tenantConfig.loginUrl);
    return <></>;
  }
  if (authClient.isAuthenticated()) {
    if (location.pathname.includes(routes.setPassword)) {
      authClient.logout();
      return <>{children}</>;
    } else {
      return <Navigate to={'/'} />;
    }
  } else {
    return <>{children}</>;
  }
};

const AppPublicRoutes: React.FunctionComponent = () => {
  const routesContent: React.ReactNode[] = useMemo(() => {
    const publicRoutes: React.ReactNode[] = [
      <Route key={'redirect'} path={routes.redirect} element={<RedirectPage />} />,
      <Route key={'login'} path={routes.login} element={<CheckAuthRoute><LoginPage /></CheckAuthRoute>} />,
      <Route key={'setPassword'} path={`${routes.setPassword}/:username/:requestCode`} element={<CheckAuthRoute><LoginPage /></CheckAuthRoute>} />,
      <Route key={'firstLogin1'} path={`${routes.firstLogin}/:username/:tempPassword`} element={<CheckAuthRoute><LoginPage /></CheckAuthRoute>} />,
      <Route key={'firstLogin2'} path={`${routes.firstLogin}/:username/`} element={<CheckAuthRoute><LoginPage /></CheckAuthRoute>} />,
      <Route key={'onlineVerification'} path={routes.onlineVerification} element={<OnlineVerification />} />,
      <Route key={'onlineVerificationSearch'} path={`${routes.onlineVerification}/:searchText`} element={<OnlineVerification />} />,
      <Route key={'publicBadgeDetails'} path={routes.publicBadgeDetails} element={<PublicBadgeDetailsPage />} />,
      <Route key={'publicBadgeDetailsName'} path={`${routes.publicBadgeDetails}/:name`} element={<PublicBadgeDetailsPage />} />,
      <Route key={'logout'} path={routes.logout} element={<LogoutLandingPage />} />,
      <Route
        key={'accessibilityStatement'}
        path={routes.accessibilityStatement}
        element={<AccessibilityStatementPage />}
      />,
    ];
    const protectedRouteContent: React.ReactNode = <MainPageLayout />;

    return [
      ...publicRoutes,
      <Route path={'*'} key={'logged-in-routes'} element={<ProtectedRoute>{protectedRouteContent}</ProtectedRoute>} />,
    ];
  }, []);
  const content: React.ReactNode = useMemo(() => {
    const result: React.ReactNode = <Routes>{routesContent}</Routes>;

    return result;
  }, [routesContent]);

  return (
    <Routes>
      <Route path={'*'} element={
        <SessionTabsContext>
          <UserGuard>
            {content}
            <SessionChecker />
          </UserGuard>
        </SessionTabsContext>
      } />
    </Routes>
  );
};
export default AppPublicRoutes;
